/**
 * PATH: packages/tenant-shared/src/preferences/quotechViewPreferenceWire.ts
 * FILE_ID: TENANTSHARED-VIEWPREF-WIRE-001
 * PURPOSE: Decode Django success/conflict envelopes without losing schema zero.
 * ROLE IN ARCHITECTURE: Pure wire adapter between quotechJson and preference store.
 * MAIN EXPORTS: toViewPreferenceEnvelope, rethrowViewPreferenceMutationError.
 * NON-RESPONSIBILITIES: Does not send HTTP, cache state, or choose identities.
 * NOTES FOR FUTURE AI: A 409 without `current` is an org-context error, not conflict.
 *   Prefer the body etag; a gzip-weakened ETag header is not the service validator.
 */

import {
  VIEW_PREFERENCE_ETAG_HEADER,
  VIEW_PREFERENCE_SCHEMA_HEADER,
  ViewPreferenceConflictError,
  normalizeViewPreferenceEtag,
} from '@quotech/api/view-preferences'
import type {
  ViewPreferenceConflictStatus,
  ViewPreferenceConflictWireEnvelope,
  ViewPreferenceEnvelope,
  ViewPreferenceWireEnvelope,
} from '@quotech/api/view-preferences'

import { QuotechApiError } from '../services/quotechApi'

export type ViewPreferenceResponse = Partial<ViewPreferenceWireEnvelope> & {
  schemaVersion?: number
}

function schemaVersionFrom(
  data: ViewPreferenceResponse | null | undefined,
  response?: Response,
): number {
  const raw =
    response?.headers.get(VIEW_PREFERENCE_SCHEMA_HEADER) ??
    data?.schema_version ??
    data?.schemaVersion
  if (raw === null || raw === undefined) return 1
  const parsed = Number(raw)
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 1
}

/** Decode one successful or nested-current envelope from body and headers. */
export function toViewPreferenceEnvelope(
  data: ViewPreferenceResponse | null | undefined,
  response?: Response,
): ViewPreferenceEnvelope {
  return {
    state:
      data?.state && typeof data.state === 'object' && !Array.isArray(data.state)
        ? data.state
        : {},
    etag:
      normalizeViewPreferenceEtag(data?.etag) ??
      normalizeViewPreferenceEtag(response?.headers.get(VIEW_PREFERENCE_ETAG_HEADER)) ??
      '*',
    schemaVersion: schemaVersionFrom(data, response),
  }
}

/**
 * Convert only typed 409/412/428 recovery bodies into store conflicts.
 *
 * Context-only 409 responses lack `current` and remain QuotechApiError.
 */
export function rethrowViewPreferenceMutationError(error: unknown): never {
  if (
    error instanceof QuotechApiError &&
    (error.status === 409 || error.status === 412 || error.status === 428)
  ) {
    const details =
      error.details as Partial<ViewPreferenceConflictWireEnvelope> | undefined
    if (details?.current && typeof details.current === 'object') {
      throw new ViewPreferenceConflictError(
        toViewPreferenceEnvelope(details.current),
        {
          status: error.status as ViewPreferenceConflictStatus,
          code: details.code,
          message: details.error || error.message,
        },
      )
    }
  }
  throw error
}
